import { useEffect } from 'react';
import './HeadbuttAnimation.css';

/**
 * PROFESSIONAL HEADBUTT ANIMATION
 * Normal-type physical move with direct head-first charge
 * 
 * Key Features:
 * - Normal-type physical attack (70 base power, 100% accuracy)
 * - Direct head-first charge attack with stunning impact
 * - Charging momentum with head enhancement and impact mechanics
 * - Professional gaming company quality with charging timing
 * - Physical move animation with charge buildup and stunning mechanics
 */
export default function HeadbuttAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container for positioning
    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();
    
    // Calculate positions
    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();
    
    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    // Calculate charge trajectory
    const dx = opponentX - playerX;
    const dy = opponentY - playerY;
    const distance = Math.hypot(dx, dy);

    // Elements to clean up
    const elements = [];

    // PHASE 1: Charge Preparation (0-500ms)
    console.log('Headbutt Phase 1: Charge Preparation');
    
    // Player enhancement with charging aura
    playerEl.classList.add('headbutt-user-charge');
    
    // Charging energy aura around player
    const chargeAura = document.createElement('div');
    chargeAura.className = 'headbutt-charge-aura';
    chargeAura.style.left = `${playerX - 85}px`;
    chargeAura.style.top = `${playerY - 85}px`;
    container.appendChild(chargeAura);
    elements.push(chargeAura);

    // Power buildup particles around player
    for (let i = 0; i < 12; i++) {
      const powerParticle = document.createElement('div');
      powerParticle.className = 'headbutt-power-particle';
      const angle = (i / 12) * 360;
      const radius = 60 + (i % 3) * 10;
      const particleX = playerX + Math.cos(angle * Math.PI / 180) * radius;
      const particleY = playerY + Math.sin(angle * Math.PI / 180) * radius;
      powerParticle.style.left = `${particleX - 4}px`;
      powerParticle.style.top = `${particleY - 4}px`;
      powerParticle.style.animationDelay = `${i * 30}ms`;
      powerParticle.textContent = '💥';
      container.appendChild(powerParticle);
      elements.push(powerParticle);
    }

    // Head enhancement effects showing preparation
    for (let i = 0; i < 8; i++) {
      const headEnhance = document.createElement('div');
      headEnhance.className = 'headbutt-head-enhance';
      const headAngle = (i / 8) * 360;
      const headRadius = 75;
      const headX = playerX + Math.cos(headAngle * Math.PI / 180) * headRadius;
      const headY = playerY + Math.sin(headAngle * Math.PI / 180) * headRadius;
      headEnhance.style.left = `${headX - 5}px`;
      headEnhance.style.top = `${headY - 5}px`;
      headEnhance.style.animationDelay = `${i * 45}ms`;
      headEnhance.textContent = '🗯️';
      container.appendChild(headEnhance);
      elements.push(headEnhance);
    }

    // Charging stance visual effect
    const chargingStance = document.createElement('div');
    chargingStance.className = 'headbutt-charging-stance';
    chargingStance.style.left = `${playerX + 20}px`;
    chargingStance.style.top = `${playerY - 15}px`;
    chargingStance.textContent = '😤💨';
    container.appendChild(chargingStance);
    elements.push(chargingStance);

    // PHASE 2: Head-First Charge (500-800ms)
    const phase2Timer = setTimeout(() => {
      console.log('Headbutt Phase 2: Head-First Charge');
      
      // Player charge movement toward opponent
      playerEl.classList.add('headbutt-user-dash');
      playerEl.style.setProperty('--dash-x', `${dx * 0.95}px`);
      playerEl.style.setProperty('--dash-y', `${dy * 0.95}px`);

      // Charging trail during dash
      for (let i = 0; i < 8; i++) {
        const trailTimer = setTimeout(() => {
          const chargeTrail = document.createElement('div');
          chargeTrail.className = 'headbutt-charge-trail';
          const progress = i / 8;
          const trailX = playerX + (dx * 0.95 * progress) + (Math.random() - 0.5) * 15;
          const trailY = playerY + (dy * 0.95 * progress) + (Math.random() - 0.5) * 15;
          chargeTrail.style.left = `${trailX - 3}px`;
          chargeTrail.style.top = `${trailY - 3}px`;
          chargeTrail.textContent = '💨';
          container.appendChild(chargeTrail);
          elements.push(chargeTrail);
        }, i * 25);
        elements.push({ timer: trailTimer, type: 'timer' });
      }

      // Speed lines during charge
      for (let i = 0; i < 6; i++) {
        const speedTimer = setTimeout(() => {
          const speedLine = document.createElement('div');
          speedLine.className = 'headbutt-speed-line';
          const speedX = playerX + (dx * 0.95 * (i / 6)) + (Math.random() - 0.5) * 30;
          const speedY = playerY + (dy * 0.95 * (i / 6)) + (Math.random() - 0.5) * 20;
          speedLine.style.left = `${speedX - 2}px`;
          speedLine.style.top = `${speedY - 15}px`;
          speedLine.style.transform = `rotate(${Math.atan2(dy, dx) * 180 / Math.PI}deg)`;
          container.appendChild(speedLine);
          elements.push(speedLine);
        }, i * 30);
        elements.push({ timer: speedTimer, type: 'timer' });
      }

      // Momentum buildup effects
      for (let i = 0; i < 5; i++) {
        const momentumTimer = setTimeout(() => {
          const momentum = document.createElement('div');
          momentum.className = 'headbutt-momentum';
          const momentumX = playerX + (dx * 0.95 * (i / 5)) + (Math.random() - 0.5) * 20;
          const momentumY = playerY + (dy * 0.95 * (i / 5)) + (Math.random() - 0.5) * 15;
          momentum.style.left = `${momentumX - 4}px`;
          momentum.style.top = `${momentumY - 4}px`;
          momentum.textContent = '⚡';
          container.appendChild(momentum);
          elements.push(momentum);
        }, i * 40);
        elements.push({ timer: momentumTimer, type: 'timer' });
      }
    }, 500);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Headbutt Impact (800-1200ms)
    const phase3Timer = setTimeout(() => {
      console.log('Headbutt Phase 3: Headbutt Impact');
      
      // Opponent hit reaction
      opponentEl.classList.add('headbutt-opponent-hit');

      // Reset player position with impact effect
      playerEl.classList.remove('headbutt-user-dash');
      playerEl.classList.add('headbutt-user-impact');

      // Massive headbutt impact
      const headbuttImpact = document.createElement('div');
      headbuttImpact.className = 'headbutt-impact';
      headbuttImpact.style.left = `${opponentX - 110}px`;
      headbuttImpact.style.top = `${opponentY - 110}px`;
      container.appendChild(headbuttImpact);
      elements.push(headbuttImpact);

      // Impact stars showing stunning effect
      for (let i = 0; i < 15; i++) {
        const impactStar = document.createElement('div');
        impactStar.className = 'headbutt-impact-star';
        const starAngle = (i / 15) * 360;
        const starRadius = 70 + Math.random() * 40;
        const starX = opponentX + Math.cos(starAngle * Math.PI / 180) * starRadius;
        const starY = opponentY + Math.sin(starAngle * Math.PI / 180) * starRadius;
        impactStar.style.left = `${starX - 4}px`;
        impactStar.style.top = `${starY - 4}px`;
        impactStar.style.animationDelay = `${i * 18}ms`;
        impactStar.style.setProperty('--scatter-x', `${Math.cos(starAngle * Math.PI / 180) * 30}px`);
        impactStar.style.setProperty('--scatter-y', `${Math.sin(starAngle * Math.PI / 180) * 30}px`);
        impactStar.textContent = '⭐';
        container.appendChild(impactStar);
        elements.push(impactStar);
      }

      // Stunning effect around opponent's head
      const stunEffect = document.createElement('div');
      stunEffect.className = 'headbutt-stun-effect';
      stunEffect.style.left = `${opponentX - 60}px`;
      stunEffect.style.top = `${opponentY - 80}px`;
      stunEffect.textContent = '😵‍💫';
      container.appendChild(stunEffect);
      elements.push(stunEffect);

      // Impact shockwaves
      for (let i = 0; i < 3; i++) {
        const shockwaveTimer = setTimeout(() => {
          const shockwave = document.createElement('div');
          shockwave.className = 'headbutt-shockwave';
          shockwave.style.left = `${opponentX - 80}px`;
          shockwave.style.top = `${opponentY - 80}px`;
          shockwave.style.animationDelay = `${i * 90}ms`;
          container.appendChild(shockwave);
          elements.push(shockwave);
        }, i * 110);
        elements.push({ timer: shockwaveTimer, type: 'timer' });
      }

      // Power burst effects
      for (let i = 0; i < 12; i++) {
        const powerBurst = document.createElement('div');
        powerBurst.className = 'headbutt-power-burst';
        const burstAngle = (i / 12) * 360;
        const burstRadius = 85;
        const burstX = opponentX + Math.cos(burstAngle * Math.PI / 180) * burstRadius;
        const burstY = opponentY + Math.sin(burstAngle * Math.PI / 180) * burstRadius;
        powerBurst.style.left = `${burstX - 4}px`;
        powerBurst.style.top = `${burstY - 4}px`;
        powerBurst.style.animationDelay = `${i * 25}ms`;
        powerBurst.textContent = '💥';
        container.appendChild(powerBurst);
        elements.push(powerBurst);
      }
    }, 800);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Recovery and Aftermath (1200-1500ms)
    const phase4Timer = setTimeout(() => {
      console.log('Headbutt Phase 4: Recovery and Aftermath');
      
      // Remove charge effects
      playerEl.classList.remove('headbutt-user-charge', 'headbutt-user-impact');
      
      // Recovery effect
      const recovery = document.createElement('div');
      recovery.className = 'headbutt-recovery';
      recovery.style.left = `${playerX - 70}px`;
      recovery.style.top = `${playerY - 70}px`;
      container.appendChild(recovery);
      elements.push(recovery);

      // Dizzy effects around opponent
      for (let i = 0; i < 6; i++) {
        const dizzy = document.createElement('div');
        dizzy.className = 'headbutt-dizzy-effect';
        const dizzyX = opponentX + (Math.random() - 0.5) * 100;
        const dizzyY = opponentY - 60 + (Math.random() - 0.5) * 40;
        dizzy.style.left = `${dizzyX - 4}px`;
        dizzy.style.top = `${dizzyY - 4}px`;
        dizzy.style.animationDelay = `${i * 40}ms`;
        dizzy.textContent = '💫';
        container.appendChild(dizzy);
        elements.push(dizzy);
      }

      // Settling dust effects
      for (let i = 0; i < 8; i++) {
        const dust = document.createElement('div');
        dust.className = 'headbutt-dust-settle';
        const dustX = opponentX + (Math.random() - 0.5) * 120;
        const dustY = opponentY + (Math.random() - 0.5) * 80;
        dust.style.left = `${dustX - 3}px`;
        dust.style.top = `${dustY - 3}px`;
        dust.style.animationDelay = `${i * 35}ms`;
        dust.textContent = '💨';
        container.appendChild(dust);
        elements.push(dust);
      }
    }, 1200);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (1500ms)
    const cleanupTimer = setTimeout(() => {
      console.log('Headbutt: Animation Complete - Cleanup');
      
      // Remove all CSS classes
      playerEl.classList.remove('headbutt-user-charge', 'headbutt-user-dash', 'headbutt-user-impact');
      opponentEl.classList.remove('headbutt-opponent-hit');
      
      // Remove all created elements
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
      
      // Animation complete
      onComplete();
    }, 1500);

    // Cleanup function for component unmount
    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('headbutt-user-charge', 'headbutt-user-dash', 'headbutt-user-impact');
      opponentEl.classList.remove('headbutt-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set move type for battle system
HeadbuttAnimation.moveType = 'normal';
